import { useNavigate } from '@tanstack/react-router';
import { Route as CommunicationsRoute } from 'src/routes/communications';
import { communicationsSearchSchema } from 'src/modules/shared/schemas/communications-search.schema';
import { type TimelineItemContext } from '../../../hooks/timeline/timeline-item-context';
import React from 'react';
import { PiChatCircle, PiFunnelSimple, PiArchive } from 'react-icons/pi';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import Tag from 'src/modules/shared/components/atoms/tag';
import { TagSkeleton } from 'src/modules/shared/components/atoms/tag/tag-skeleton';
import { truncate } from 'src/modules/shared/utils/formatting/text.util';

interface TimelineContextBadgesProps {
  context?: TimelineItemContext;
}

/**
 * Determines context type and returns the appropriate icon.
 * For archived conversations, uses PiArchive.
 * Otherwise, returns context-specific icon: PiFunnelSimple for funnel, PiChatCircle for conversation.
 */
function resolveContextIcon(
  isArchived: boolean,
  isFunnelContext: boolean
): React.ReactNode {
  if (isArchived) {
    return <PiArchive className="w-3.5 h-3.5" />;
  }

  if (isFunnelContext) {
    return <PiFunnelSimple className="w-3.5 h-3.5" />;
  }

  return <PiChatCircle className="w-3.5 h-3.5" />;
}

/**
 * Renders cross-context indicators (badges) for timeline items and handles navigation
 * to the referenced funnel conversation using the route search schema.
 */
export const TimelineContextBadges: React.FC<TimelineContextBadgesProps> = ({
  context,
}) => {
  const navigate = useNavigate();

  if (!context) {
    return null;
  }

  if (context.loading) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <TagSkeleton size="sm" variant="neutral" className="w-[6rem]" />
      </div>
    );
  }

  if (!context.otherFunnel && !context.otherConversation) {
    return null;
  }

  const goToContextConversation = () => {
    const targetConversationId = context.otherConversation?.id;
    const targetFunnelId = context.otherFunnel?.id;
    if (!targetConversationId && !targetFunnelId) return;

    void navigate({
      to: CommunicationsRoute.to,
      search: (prev) => {
        const parsed = communicationsSearchSchema.parse(prev);
        const funnelId = targetFunnelId ?? parsed.funnelId;
        const conversationId = targetConversationId ?? parsed.conversationId;

        return {
          ...parsed,
          funnelId,
          conversationId,
        };
      },
    });
  };

  const isArchived = !!context.otherConversation?.archivedAt;
  const isFunnelContext = !!context.otherFunnel;

  /**
   * Determines content based on context type (funnel vs conversation).
   */
  const badgeContent = isFunnelContext
    ? context.otherFunnel?.name
      ? // TODO: check when implementing i18n
        truncate(`En ${context.otherFunnel.name}`)
      : truncate('En otro embudo')
    : truncate('En otra conversación');

  const shouldRender = context.otherFunnel ?? context.otherConversation;

  /**
   * Build tooltip content showing destination conversation info.
   */
  const tooltipContent = (): string => {
    // TODO: check when implementing i18n
    let text = 'Ir a conversación';
    if (isArchived) {
      // TODO: check when implementing i18n
      text += ' archivada';
    }
    if (context.otherFunnel?.name) {
      // TODO: check when implementing i18n
      text += ` en ${context.otherFunnel.name}`;
    }
    return text;
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shouldRender && (
        // TODO: check when implementing i18n
        <Tooltip size="sm" content={tooltipContent()} placement="top">
          <Tag
            size="sm"
            variant={isArchived ? 'neutral' : 'info'}
            appearance="ghost"
            icon={resolveContextIcon(isArchived, isFunnelContext)}
            content={badgeContent}
            onClick={goToContextConversation}
          />
        </Tooltip>
      )}
    </div>
  );
};
